import type { FavoritePlace } from '@/types/favoritePlace'
import type { TourListItem } from '@/types/tour'

const DEV_MAPTILER_KEY = 'sdk0PvYejJ9OWgzt5A4Z'
const MAPTILER_GEOCODING_KEY = import.meta.env.VITE_MAPTILER_KEY || DEV_MAPTILER_KEY

export type SmartSearchAppResult =
  | {
      kind: 'map-search'
      id: string
      title: string
      subtitle: string
    }
  | {
      kind: 'favorite-place'
      id: string
      title: string
      subtitle: string
      lat: number
      lon: number
      placeType: string | null
      favoritePlaceId: number
      updatedAt: string
    }
  | {
      kind: 'workspace'
      id: string
      title: string
      subtitle: string
      workspaceId: string
    }
  | {
      kind: 'workspace-tour'
      id: string
      title: string
      subtitle: string
      workspaceId: string
      tourId: string
    }
  | {
      kind: 'workspace-track'
      id: string
      title: string
      subtitle: string
      workspaceId: string
      tourId: string
      trackClientId: string
    }
  | {
      kind: 'library-tour'
      id: string
      title: string
      subtitle: string
      tour: TourListItem
    }

export interface SmartSearchPlaceResult {
  kind: 'place'
  id: string
  title: string
  subtitle: string
  lat: number
  lon: number
  placeType: string | null
  sourceId: string | null
}

export interface SmartSearchLocationEventDetail {
  flyTo: {
    lat: number
    lon: number
    zoom?: number
  }
  pin?: {
    id: string
    title: string
    subtitle?: string
    lat: number
    lon: number
  }
  name?: string
  description?: string
  type?: string
}

export interface SmartSearchViewportBias {
  proximity?: {
    lon: number
    lat: number
  }
}

interface MapTilerFeature {
  id?: string | number
  text?: string
  place_name?: string
  center?: [number, number]
  place_type?: string[]
}

const normalizeSearchText = (value: string | null | undefined) =>
  (value ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const escapeMapSearchLabel = (query: string) => `Search "${query}" on the map...`

const roundPlaceCoordinate = (value: number) => Math.round(value * 100000) / 100000

export function buildSmartSearchPlaceKey(place: {
  title: string
  subtitle: string
  lat: number
  lon: number
}): string {
  return [
    normalizeSearchText(place.title),
    normalizeSearchText(place.subtitle),
    roundPlaceCoordinate(place.lat),
    roundPlaceCoordinate(place.lon),
  ].join('|')
}

export function buildSmartSearchMapSearchAction(
  query: string,
): Extract<SmartSearchAppResult, { kind: 'map-search' }> {
  const trimmedQuery = query.trim()
  return {
    kind: 'map-search',
    id: `map-search:${trimmedQuery}`,
    title: escapeMapSearchLabel(trimmedQuery),
    subtitle: 'Open MapTiler location search results',
  }
}

export function mapFavoritePlaceToSmartSearchFavoriteResult(
  favoritePlace: FavoritePlace,
): Extract<SmartSearchAppResult, { kind: 'favorite-place' }> {
  return {
    kind: 'favorite-place',
    id: `favorite-place:${favoritePlace.id}`,
    title: favoritePlace.title,
    subtitle: favoritePlace.subtitle,
    lat: favoritePlace.lat,
    lon: favoritePlace.lon,
    placeType: favoritePlace.placeType,
    favoritePlaceId: favoritePlace.id,
    updatedAt: favoritePlace.updatedAt,
  }
}

export async function searchMaptilerPlaces(
  query: string,
  options: { signal?: AbortSignal; limit?: number; viewportBias?: SmartSearchViewportBias } = {},
): Promise<SmartSearchPlaceResult[]> {
  const trimmedQuery = query.trim()
  if (!trimmedQuery) return []

  const url = new URL(`https://api.maptiler.com/geocoding/${encodeURIComponent(trimmedQuery)}.json`)
  url.searchParams.set('key', MAPTILER_GEOCODING_KEY)
  url.searchParams.set('language', 'en')
  url.searchParams.set('autocomplete', 'true')
  url.searchParams.set('limit', String(options.limit ?? 8))
  if (options.viewportBias?.proximity) {
    url.searchParams.set(
      'proximity',
      `${options.viewportBias.proximity.lon},${options.viewportBias.proximity.lat}`,
    )
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    signal: options.signal,
  })

  if (!response.ok) {
    throw new Error(`Map search failed with status ${response.status}`)
  }

  const payload = (await response.json()) as { features?: MapTilerFeature[] }

  return (payload.features ?? [])
    .filter((feature): feature is MapTilerFeature & { center: [number, number] } => {
      return (
        Array.isArray(feature.center) &&
        typeof feature.center[0] === 'number' &&
        typeof feature.center[1] === 'number'
      )
    })
    .map((feature, index) => ({
      kind: 'place' as const,
      id: `place:${feature.id ?? index}`,
      title: feature.text || feature.place_name || trimmedQuery,
      subtitle: feature.place_name || '',
      lon: feature.center[0],
      lat: feature.center[1],
      placeType: feature.place_type?.[0] ?? null,
      sourceId: feature.id != null ? String(feature.id) : null,
    }))
}
